import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { OAuthService } from "angular-oauth2-oidc";
import { catchError, finalize, map } from "rxjs";
import { environment } from "src/environments/environment";
import { Test } from "../domain/test";
import { CoreData } from "../tools/core-data/core-data";
import { mapper } from "../tools/mapping/mapper";
import { LoadingTask } from "../tools/ui/loading-task";
import { BaseService } from "./base-service";
import { TestDTO } from "./dto/test.dto";
import { AppConfig } from "../app.config";

@Injectable({
    providedIn: 'root',
})
export class TestService extends BaseService {

    private serviceUrl = `${this.config.getConfig('serviceBaseUrl')}api/test`;

    constructor(public http: HttpClient,
        protected override coreData: CoreData,
        protected override snackBar: MatSnackBar,
        protected override oauthService: OAuthService,
        private loadingTask: LoadingTask,
        protected override router: Router,
        private config: AppConfig) {
        super(router, coreData, oauthService, snackBar);
    }

    getTestByCode(code: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/${code}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getTestByCode')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: TestDTO) => {
                return mapper.map(response, TestDTO, Test);
            }));
    }

    getTestByTestGroup(testGroupCode: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/byTestGroup/${testGroupCode}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getTestByTestGroup')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: TestDTO[]) => {
                return response.map((item: TestDTO) => {
                    return mapper.map(item, TestDTO, Test);
                });
            }));
    }

    updatePosition(testGroupCode: string, testList: Test[]) {
        this.loadingTask.activateLoading();
        return this.http.put(`${this.serviceUrl}/byTestGroup/${testGroupCode}`, testList, this.generateHeader()).pipe(
            catchError(this.handleError<any>('updatePosition')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    create(t: Test, testGroupCode: string) {

        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/${testGroupCode}`, t, this.generateHeader()).pipe(
            catchError(this.handleError<any>('create')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }

    update(t: Test) {

        this.loadingTask.activateLoading();
        return this.http.put(`${this.serviceUrl}`, t, this.generateHeader()).pipe(
            catchError(this.handleError<any>('update')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: string) => {
                return response;
            }));
    }

    delete(testCode: string) {

        this.loadingTask.activateLoading();
        return this.http.delete(`${this.serviceUrl}/${testCode}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('delete')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    deleteAll(test: Test[]) {

        this.loadingTask.activateLoading();
        let opt = Object.assign({}, this.generateHeader()) as any;
        opt.body = test.map(x => x.Code)
        return this.http.delete(`${this.serviceUrl}/`, opt).pipe(
            catchError(this.handleError<any>('delete')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    changeTestToNewGroup(testCode: string[], groupDestinationCode: string) {

        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/move/${groupDestinationCode}`, testCode, this.generateHeader()).pipe(
            catchError(this.handleError<any>('changeTestToNewGroup')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean) => {
                return response;
            }));
    }

    getTestAvailableOnBatch(productGroupCode: string, batchCode: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/byBatch/${productGroupCode}/${batchCode}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getTestAvailableOnBatch')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: TestDTO[]) => {
                return response.map((item: TestDTO) => {
                    return mapper.map(item, TestDTO, Test);
                });
            }));
    }
}